import { Link } from "react-router";
import type { UpcomingMaintenanceItem } from "../types";
import { AlertTriangle, Clock, CheckCircle, CalendarCheck } from "lucide-react";

interface UpcomingMaintenanceWidgetProps {
  items: UpcomingMaintenanceItem[];
}

function getUrgencyIcon(item: UpcomingMaintenanceItem) {
  if (item.isOverdue) {
    return (
      <span role="img" aria-label="Overdue">
        <AlertTriangle className="w-4 h-4 text-red-500" aria-hidden="true" />
      </span>
    );
  }
  if (
    (item.daysUntilDue != null && item.daysUntilDue <= 30) ||
    (item.milesUntilDue != null && item.milesUntilDue <= 1000)
  ) {
    return (
      <span role="img" aria-label="Due soon">
        <Clock className="w-4 h-4 text-yellow-500" aria-hidden="true" />
      </span>
    );
  }
  return (
    <span role="img" aria-label="On track">
      <CheckCircle className="w-4 h-4 text-green-500" aria-hidden="true" />
    </span>
  );
}

function getUrgencyBg(item: UpcomingMaintenanceItem) {
  if (item.isOverdue) return "bg-red-50";
  if (
    (item.daysUntilDue != null && item.daysUntilDue <= 30) ||
    (item.milesUntilDue != null && item.milesUntilDue <= 1000)
  ) {
    return "bg-yellow-50";
  }
  return "bg-green-50";
}

export function UpcomingMaintenanceWidget({ items }: UpcomingMaintenanceWidgetProps) {
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Upcoming Maintenance</h3>
          <Link
            to="/schedules"
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            View All
          </Link>
        </div>
      </div>
      <div className="p-6">
        {items.length === 0 ? (
          <div className="text-center py-8">
            <CalendarCheck className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No upcoming maintenance</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <Link
                key={`${item.vehicleId}-${item.scheduleId}`}
                to={`/schedules?vehicle=${item.vehicleId}`}
                className={`block p-4 rounded-lg ${getUrgencyBg(item)} hover:opacity-80 transition-opacity`}
              >
                <div className="flex items-start justify-between mb-1">
                  <p className="font-medium text-gray-900">{item.serviceName}</p>
                  {getUrgencyIcon(item)}
                </div>
                <p className="text-sm text-gray-600">{item.vehicleName}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  {item.nextDueDate && (
                    <span>
                      {new Date(item.nextDueDate).toLocaleDateString()}
                      {item.daysUntilDue != null && (
                        <span className="ml-1">
                          ({item.daysUntilDue < 0
                            ? `${Math.abs(item.daysUntilDue)}d overdue`
                            : `in ${item.daysUntilDue}d`})
                        </span>
                      )}
                    </span>
                  )}
                  {item.nextDueMileage && (
                    <span>
                      {item.nextDueMileage.toLocaleString()} mi
                      {item.milesUntilDue != null && (
                        <span className="ml-1">
                          ({item.milesUntilDue < 0
                            ? `${Math.abs(item.milesUntilDue).toLocaleString()} mi over`
                            : `${item.milesUntilDue.toLocaleString()} mi left`})
                        </span>
                      )}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
